/* eslint-disable */
import { Calendar, Check, Eye, Package, Pencil, Store, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CheckPicker } from 'rsuite';
import { Modal } from '@mui/material';
import DraggableImageContainer from '@/Components/DraggableImageContainer/DraggableImageContainer';
import VariantTable from '../VariantPrice/VariantTable';

// Product Info Modal Component (View Mode)
const ProductInfoModal = (props) => {
    const { show = false, data = {}, onClose = () => { } } = props;
    return (
        show && (
            <div
                className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]'
                onClick={onClose}
            >
                <div
                    className='bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-hidden'
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Popup Header */}
                    <div className='flex items-center justify-between mb-4 pb-3 border-b border-gray-200'>
                        <div>
                            <h3 className='text-lg font-bold text-gray-800'>
                                {data?.groupName}
                            </h3>
                            <span className='text-sm text-gray-500'>
                                {data?.products?.length || 0} products
                            </span>
                        </div>
                        <button
                            onClick={onClose}
                            className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                        >
                            <X size={20} className='text-gray-500' />
                        </button>
                    </div>

                    {/* Product List */}
                    <div className='overflow-y-auto max-h-[50vh]'>
                        {data?.products?.length > 0 ? (
                            <div className='space-y-3'>
                                {data.products.map((product, index) => (
                                    <div
                                        key={product?.id || index}
                                        className='flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors'
                                    >
                                        {product?.productImageUrl ? (
                                            <img
                                                src={product.productImageUrl}
                                                alt={product.name || product.productName}
                                                className='w-12 h-12 rounded-lg object-cover'
                                            />
                                        ) : (
                                            <div className='w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center'>
                                                <span className='text-yellow-600 font-bold text-lg'>
                                                    {(product?.name || product?.productName || 'P').charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                        )}
                                        <div className='flex-1'>
                                            <p className='font-medium text-gray-800'>
                                                {product?.name || product?.productName || 'Unknown Product'}
                                            </p>
                                            {product?.sku && (
                                                <p className='text-sm text-gray-500'>SKU: {product.sku}</p>
                                            )}
                                        </div>
                                        <span className='text-sm font-medium text-gray-600 bg-gray-200 px-2 py-1 rounded'>
                                            #{index + 1}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className='text-center py-8 text-gray-500'>
                                <p className='text-lg'>No products added</p>
                                <p className='text-sm'>Add products to this group first</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    );
};

const ProductEditModal = (props) => {
    const { show = false, onClose = () => { }, data = null, onProductChange = () => { }, getContainerLists = {} } = props;
    return (
        <Modal
            open={show}
            onClose={onClose}
        >
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90vw',
                maxWidth: '1200px',
                height: '80vh',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 0 10px rgba(0,0,0,0.15)',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {/* Modal Header */}
                <div className='flex items-center justify-between mb-4 pb-3 border-b border-gray-200'>
                    <div>
                        <h3 className='text-lg font-bold text-gray-800'>
                            Edit Products - {data?.groupName}
                        </h3>
                        <span className='text-sm text-gray-500'>
                            Drag or click products to add/remove
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                    >
                        <X size={20} className='text-gray-500' />
                    </button>
                </div>

                {/* DraggableImageContainer */}
                <div className='flex-1 overflow-hidden'>
                    {data && (
                        <DraggableImageContainer
                            firstContainer={{
                                list: getContainerLists.first,
                                title: 'Available Products',
                                showSearchBar: true,
                                placeholder: 'Search products...',
                            }}
                            secondContainer={{
                                list: getContainerLists.second,
                                title: 'Selected Products',
                                showSearchBar: true,
                                placeholder: 'Search selected...',
                            }}
                            getContainersList={onProductChange}
                        />
                    )}
                </div>

                <div className='flex items-center justify-end gap-3 mt-4 pt-3 border-t border-gray-200'>
                    <button
                        onClick={onClose}
                        className='px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-colors'
                    >
                        Done
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// Day Toggle Button Component
const DayToggleButton = ({ isActive, onClick, disabled }) => {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${isActive
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            title={isActive ? 'Scheduled' : 'Not scheduled'}
        >
            {isActive ? <Check size={14} /> : <X size={14} />}
        </button>
    );
};

const GroupView = React.memo(function GroupView(props) {
    const {
        data = [],
        productList = [],
        days = [],
        isEditMode = false,
        onSave = () => { },
    } = props;

    const stores = useSelector((state) => state?.form?.STORES?.data?.LIST);
    const [viewProductsModal, setViewProductsModal] = useState({ show: false, data: null });
    const [editProductsModal, setEditProductsModal] = useState({ show: false, group: null });
    const [editScheduleData, setEditScheduleData] = useState({});


    const storeOptions = useMemo(() => {
        return stores?.map((store) => ({
            label: store.name,
            value: store.id,
        })) || [];
    }, [stores]);

    useEffect(() => {
        if (isEditMode && data?.length > 0) {
            const initialData = {};
            data.forEach((group) => {
                initialData[group.id] = {
                    stores: group.storeIds || [],
                    days: (group.configValues || []).reduce((acc, day) => {
                        acc[day] = true;
                        return acc;
                    }, {}),
                    productIds: group.productIds || [],
                };
            });
            setEditScheduleData(initialData);
        }
    }, [isEditMode, data]);

    // Handle store change in edit mode
    const handleStoreChange = useCallback((groupId, selectedStores) => {
        setEditScheduleData((prev) => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                stores: selectedStores,
            },
        }));
    }, []);

    // Handle day toggle in edit mode
    const handleDayToggle = useCallback((groupId, day) => {
        setEditScheduleData((prev) => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                days: {
                    ...prev[groupId]?.days,
                    [day]: !prev[groupId]?.days?.[day],
                },
            },
        }));
    }, []);

    const handleProductListChange = useCallback((containerList) => {
        if (!editProductsModal.group) return;
        const groupId = editProductsModal.group.id;
        const newProductIds = containerList.secondContainerList.map((item) => item.id);

        setEditScheduleData((prev) => ({
            ...prev,
            [groupId]: {
                ...prev[groupId],
                productIds: newProductIds,
            },
        }));
    }, [editProductsModal.group]);

    const getEditedData = useCallback(() => {
        return data.map((group) => {
            const editData = editScheduleData[group.id];
            if (!editData) return group;

            const activeDays = Object.entries(editData.days || {})
                .filter(([_, isActive]) => isActive)
                .map(([day]) => parseInt(day));

            return {
                groupId: group.id,
                groupName: group.name,
                productIds: editData.productIds || group.productIds,
                configType: 'dates_of_month',
                configValues: activeDays,
                storeIds: editData.stores,
            };
        });
    }, [data, editScheduleData]);

    useEffect(() => {
        if (isEditMode) {
            onSave(getEditedData);
        }
    }, [isEditMode, getEditedData, onSave]);

    const handleViewProducts = useCallback((row) => {
        const products = row?.productIds?.map((productId) =>
            productList?.find((product) => product?.id === productId)
        ).filter(Boolean) || [];

        setViewProductsModal({
            show: true,
            data: {
                groupName: row?.name || row?.groupName,
                products: products,
            }
        });
    }, [productList]);

    const closeViewProductsModal = useCallback(() => {
        setViewProductsModal({ show: false, data: null });
    }, []);

    const handleEditProducts = useCallback((row) => {
        setEditProductsModal({ show: true, group: row });
    }, []);

    const closeEditProductsModal = useCallback(() => {
        setEditProductsModal({ show: false, group: null });
    }, []);

    const getContainerLists = useMemo(() => {
        if (!editProductsModal.group) return { first: [], second: [] };

        const groupId = editProductsModal.group.id;
        const currentProductIds = editScheduleData[groupId]?.productIds || editProductsModal.group.productIds || [];

        const selectedProducts = productList?.filter((product) =>
            currentProductIds.includes(product?.id)
        ) || [];

        const availableProducts = productList?.filter((product) =>
            !currentProductIds.includes(product?.id)
        ) || [];

        return { first: availableProducts, second: selectedProducts };
    }, [editProductsModal.group, editScheduleData, productList]);

    const viewScheduleColumns = useMemo(() => {
        const baseColumns = [
            { key: 'sno', title: 'S.NO', fixed: true, width: 60, customCol: true },
            { key: 'groupName', title: 'GROUP NAME', fixed: true, width: 200, customCol: true },
            { key: 'store', title: 'STORE', width: isEditMode ? 220 : 200, customCol: true },
        ];
        const dayColumns = days.map((day) => ({
            key: `day_${day}`,
            title: day,
            customCol: true,
            width: 55,
        }));
        return [...baseColumns, ...dayColumns];
    }, [days, isEditMode]);

    const viewScheduleColMapping = useMemo(() => ({
        sno: 'sno',
        groupName: 'groupName',
        store: 'store',
        ...days.reduce((acc, day) => { acc[`day_${day}`] = `day_${day}`; return acc; }, {}),
    }), [days]);

    const viewScheduleCustomCol = useCallback((column) => {
        if (column.key === 'sno') return <span className='font-semibold text-xs'>S.NO</span>;
        if (column.key === 'groupName') return (
            <div className='flex items-center gap-1'>
                <Package size={14} className='text-yellow-600' />
                <span className='font-semibold text-xs'>GROUP NAME</span>
            </div>
        );
        if (column.key === 'store') return (
            <div className='flex items-center gap-1'>
                <Store size={14} className='text-yellow-600' />
                <span className='font-semibold text-xs'>STORE</span>
            </div>
        );
        if (column.key.startsWith('day_')) {
            const day = parseInt(column.key.split('_')[1]);
            return (
                <div className='flex items-center gap-0.5'>
                    <Calendar size={12} className='text-yellow-600' />
                    <span className='font-semibold text-xs'>{day}</span>
                </div>
            );
        }
        return column.title;
    }, []);

    const viewScheduleCustomRowData = useMemo(() => {
        const dayRenderers = days.reduce((acc, day) => {
            acc[`day_${day}`] = (row) => {
                if (isEditMode) {
                    const isActive = editScheduleData[row.id]?.days?.[day] || false;
                    return (
                        <DayToggleButton
                            isActive={isActive}
                            onClick={() => handleDayToggle(row.id, day)}
                        />
                    );
                }
                const isActive = row.configValues?.includes(day) || false;
                return (
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${isActive ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                        }`}>
                        {isActive ? <Check size={14} /> : <X size={14} />}
                    </div>
                );
            };
            return acc;
        }, {});

        return {
            sno: (row) => <span className='font-medium text-gray-700 text-sm'>{row.sno}</span>,
            groupName: (row) => {
                const currentProductCount = isEditMode
                    ? (editScheduleData[row.id]?.productIds?.length ?? row.productIds?.length ?? 0)
                    : (row.productCount || row.productIds?.length || 0);

                return (
                    <div className='flex flex-col gap-1'>
                        <div className='flex items-center gap-2'>
                            <span className='text-sm font-semibold text-gray-800'>{row.name}</span>
                            {isEditMode ? (
                                <button
                                    onClick={() => handleEditProducts(row)}
                                    className='p-1 hover:bg-blue-100 rounded-full transition-colors'
                                    title='Edit Products'
                                >
                                    <Pencil size={16} className='text-blue-500 hover:text-blue-600' />
                                </button>
                            ) : (
                                <button
                                    onClick={() => handleViewProducts(row)}
                                    className='p-1 hover:bg-blue-100 rounded-full transition-colors'
                                    title='View Products'
                                >
                                    <Eye size={16} className='text-blue-500 hover:text-blue-600' />
                                </button>
                            )}
                        </div>
                        <span className='text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full w-fit'>
                            {currentProductCount} products
                        </span>
                    </div>
                );
            },
            store: (row) => {
                if (isEditMode) {
                    return (
                        <CheckPicker
                            data={storeOptions}
                            value={editScheduleData[row.id]?.stores || []}
                            onChange={(value) => handleStoreChange(row.id, value)}
                            placeholder='Select Stores'
                            style={{ width: 200, fontSize: '14px' }}
                            size='sm'
                            searchable={true}
                            cleanable={true}
                            placement='bottomStart'
                            menuStyle={{ zIndex: 9999 }}
                        />
                    );
                }
                return (
                    <div className='flex flex-col gap-1 justify-start'>
                        {row.storeNames?.length > 0 ? row.storeNames.map((name, idx) => (
                            <span key={idx} className='bg-yellow-100 w-fit text-yellow-800 text-xs px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0'>
                                {name}
                            </span>
                        )) : <span className='text-gray-400 text-xs'>No stores</span>}
                    </div>
                );
            },
            ...dayRenderers,
        };
    }, [days, isEditMode, editScheduleData, storeOptions, handleStoreChange, handleDayToggle, handleViewProducts, handleEditProducts]);

    return (
        <div className='h-full w-full'>
            {/* View Products Modal (View Mode) */}
            <ProductInfoModal
                show={viewProductsModal?.show}
                data={viewProductsModal?.data}
                onClose={closeViewProductsModal}
            />
            <ProductEditModal
                show={editProductsModal?.show}
                onClose={closeEditProductsModal}
                data={editProductsModal?.group}
                onProductChange={handleProductListChange}
                getContainerLists={getContainerLists}
            />
            <VariantTable
                data={data}
                columns={viewScheduleColumns}
                colMapping={viewScheduleColMapping}
                customRowData={viewScheduleCustomRowData}
                customCol={viewScheduleCustomCol}
            />
        </div>
    );
});

export default GroupView;